import React, { useRef } from "react";
import {
  IconButton,
  Popover,
  Typography,
  Tooltip,
  Divider,
  Button,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import WallpaperIcon from "@mui/icons-material/Wallpaper";
import { useChatBackground } from "../../hooks/useChatBackground";

const COLORS = [
  { label: "Padrão", value: null },
  { label: "Branco", value: "#FFFFFF" },
  { label: "Cinza claro", value: "#F0F0F0" },
  { label: "Bege", value: "#F5F0E8" },
  { label: "Verde claro", value: "#DCF8C6" },
  { label: "Azul claro", value: "#DDEEFF" },
  { label: "Rosa claro", value: "#FDDDE6" },
  { label: "Lavanda", value: "#EDE7F6" },
  { label: "Cinza escuro", value: "#37474F" },
  { label: "Azul escuro", value: "#1A237E" },
  { label: "Preto", value: "#121212" },
];

const useStyles = makeStyles((theme) => ({
  popover: {
    padding: theme.spacing(2),
    width: 260,
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    fontSize: 13,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
  },
  colorGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: theme.spacing(2),
  },
  colorDot: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    cursor: "pointer",
    border: "2px solid transparent",
    position: "relative",
    flexShrink: 0,
    "&:hover": { opacity: 0.85, transform: "scale(1.1)" },
    transition: "transform 0.15s",
  },
  activeDot: {
    border: "2px solid #F37021",
    boxShadow: "0 0 0 2px rgba(243,112,33,0.3)",
  },
  defaultDot: {
    background: "linear-gradient(135deg, #e0e0e0 25%, #bdbdbd 100%)",
    border: "2px dashed #9e9e9e",
  },
  uploadSection: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  previewBox: {
    width: "100%",
    height: 60,
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
}));

const ChatBackgroundPicker = () => {
  const classes = useStyles();
  const { bg, setBackground } = useChatBackground();
  const [anchor, setAnchor] = React.useState(null);
  const fileRef = useRef();

  const handleColor = (color) => {
    if (!color) {
      setBackground(null);
    } else {
      setBackground({ type: "color", value: color });
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Imagem muito grande. Máximo 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setBackground({ type: "image", value: ev.target.result });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const isActive = (color) => {
    if (!color) return !bg;
    return bg?.type === "color" && bg?.value === color;
  };

  return (
    <>
      <Tooltip title="Fundo do chat">
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
          <WallpaperIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <div className={classes.popover}>
          <Typography className={classes.title}>Cor de fundo</Typography>
          <div className={classes.colorGrid}>
            {COLORS.map(({ label, value }) => (
              <Tooltip key={label} title={label}>
                <div
                  className={`${classes.colorDot} ${isActive(value) ? classes.activeDot : ""} ${!value ? classes.defaultDot : ""}`}
                  style={value ? { backgroundColor: value } : {}}
                  onClick={() => handleColor(value)}
                />
              </Tooltip>
            ))}
          </div>

          <Divider style={{ marginBottom: 12 }} />

          <Typography className={classes.title}>Imagem personalizada</Typography>
          <div className={classes.uploadSection}>
            {bg?.type === "image" && (
              <div
                className={classes.previewBox}
                style={{ backgroundImage: `url(${bg.value})` }}
              />
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFile}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => fileRef.current?.click()}
              fullWidth
            >
              {bg?.type === "image" ? "Trocar imagem" : "Escolher imagem"}
            </Button>
            {bg && (
              <Button
                size="small"
                onClick={() => setBackground(null)}
                fullWidth
                style={{ color: "#999" }}
              >
                Remover fundo
              </Button>
            )}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default ChatBackgroundPicker;
